import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  writeBatch 
} from 'firebase/firestore';
import { db } from '../../data/firebaseConfig';

const CoursesDataTest = {
  // Obtener todos los cursos
  getAllCourses: async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'academyCourses'));
      const courses = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      return courses;
    } catch (error) {
      console.error('Error al obtener cursos:', error);
      throw error;
    }
  },

  // Obtener un curso por su ID
  getCourseById: async (courseId) => {
    try {
      const courseRef = doc(db, 'academyCourses', courseId);
      const courseSnap = await getDoc(courseRef);
      
      if (courseSnap.exists()) {
        return { id: courseSnap.id, ...courseSnap.data() };
      } else {
        throw new Error('Curso no encontrado');
      }
    } catch (error) {
      console.error('Error al obtener curso por ID:', error);
      throw error;
    }
  },

  // Función auxiliar para generar ID único
  generateUniqueId: () => {
    return `${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
  },

  // Función auxiliar para crear starterGuides para las actividades
  createStarterGuidesForActivities: async (courseId, courseName, modules) => {
    if (!courseId || !courseName || !Array.isArray(modules)) {
      throw new Error('Parámetros inválidos para crear starterGuides');
    }

    const batch = writeBatch(db);
    const createdGuides = [];

    try {
      for (let moduleIndex = 0; moduleIndex < modules.length; moduleIndex++) {
        const module = modules[moduleIndex];
        
        if (module.lessons?.[0]?.classActivities) {
          for (let activityIndex = 0; activityIndex < module.lessons[0].classActivities.length; activityIndex++) {
            const activity = module.lessons[0].classActivities[activityIndex];
            
            if (!activity.idStart || !activity.id) {
              console.warn(`Actividad sin ID válido en módulo ${moduleIndex}, actividad ${activityIndex}`);
              continue;
            }

            // Crear documento starterGuide con ID específico
            const starterGuideRef = doc(db, 'starterGuides', activity.idStart);
            const starterGuideData = {
              // Campos principales de identificación
              id: activity.idStart,
              activityId: activity.id,
              courseId: courseId,
              courseName: courseName,
              moduleIndex: moduleIndex,
              moduleNumber: module.number || (moduleIndex + 1).toString(),
              moduleName: module.name || module.alias || `Módulo ${module.number || moduleIndex + 1}`,
              activityIndex: activityIndex,
              activityName: activity.name,
              
              // Estructura del contenido del starterGuide
              name: activity.name || 'Actividad sin nombre',
              InitialProjectDescription: activity.InitialProjectDescription || activity.introductionText || `En esta actividad trabajaremos con ${activity.name}`,
              descriptions: activity.descriptions || `Descripción de la actividad: ${activity.name}`,
              
              
              // Proyecto con código
              project: {
                code: activity.code || "# Código del proyecto\nconsole.log('Proyecto iniciado');"
              },
              
              // Pasos de la guía
              steps: activity.steps || [
                {
                  description: "Configura los componentes básicos para la actividad.",
                }
              ],
              
              // Metadatos
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };

            batch.set(starterGuideRef, starterGuideData);
            createdGuides.push({
              id: starterGuideRef.id,
              idStart: activity.idStart,
              activityId: activity.id,
              activityName: activity.name
            });
          }
        }
      }

      // Ejecutar todas las operaciones en batch
      if (createdGuides.length > 0) {
        await batch.commit();
      }
      return createdGuides;
    } catch (error) {
      console.error('Error al crear starterGuides:', error);
      throw error;
    }
  },

  cleanUndefinedFields: (obj) => {
    if (obj === null || obj === undefined) return null;
    
    if (Array.isArray(obj)) {
      return obj.map(item => CoursesDataTest.cleanUndefinedFields(item));
    }
    
    if (typeof obj === 'object') {
      const cleanedObj = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined && value !== null) {
          cleanedObj[key] = CoursesDataTest.cleanUndefinedFields(value);
        }
      }
      return cleanedObj;
    }
    
    return obj;
  },

  updateCourseFields: async (courseId, updateData) => {
    try {
      console.log(`Actualizando campos del curso ${courseId}:`, updateData);
      
      const courseRef = doc(db, 'academyCourses', courseId);
      
      // LIMPIAR DATOS: Remover campos undefined antes de actualizar
      const cleanUpdateData = CoursesDataTest.cleanUndefinedFields({
        ...updateData,
        updatedAt: new Date().toISOString()
      });

      console.log('Datos limpios para actualizar campos:', cleanUpdateData);
      
      await updateDoc(courseRef, cleanUpdateData);
      
      console.log(`✅ Curso ${courseId} actualizado exitosamente`);
      return true;
      
    } catch (error) {
      console.error('Error actualizando campos del curso:', error);
      throw new Error(`No se pudo actualizar el curso: ${error.message}`);
    }
  },
  // método específico para banner
  updateCourseBanner: async (courseId, bannerData) => {
    try {
      console.log(`Actualizando banner del curso ${courseId}`);
      
      const courseRef = doc(db, 'academyCourses', courseId);
      
      const updateData = {
        imageBanner: bannerData.bannerImage,
        backgroundColor: bannerData.backgroundColor,
        institutionLogo: bannerData.institutionLogo,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(courseRef, updateData);
      
      console.log(`✅ Banner del curso ${courseId} actualizado exitosamente`);
      return true;
      
    } catch (error) {
      console.error('Error actualizando banner del curso:', error);
      throw new Error(`No se pudo actualizar el banner: ${error.message}`);
    }
  },

  //Crea practical Guides
  createPracticalGuidesForActivities: async (courseId, courseName, modules) => {
    if (!courseId || !courseName || !Array.isArray(modules)) {
      throw new Error('Parámetros inválidos para crear practicalGuides');
    }

    const batch = writeBatch(db);
    const createdGuides = [];

    try {
      for (let moduleIndex = 0; moduleIndex < modules.length; moduleIndex++) {
        const module = modules[moduleIndex];
        
        if (module.lessons?.[0]?.classActivities) {
          for (let activityIndex = 0; activityIndex < module.lessons[0].classActivities.length; activityIndex++) {
            const activity = module.lessons[0].classActivities[activityIndex];
            
            if (!activity.id) {
              console.warn(`Actividad sin ID válido en módulo ${moduleIndex}, actividad ${activityIndex}`);
              continue;
            }

            // CAMBIO IMPORTANTE: Obtener datos del starterGuide existente
            let starterGuideData = null;
            try {
              if (activity.idStart) {
                starterGuideData = await CoursesDataTest.getStarterGuideByIdStart(activity.idStart);
                console.log(`StarterGuide encontrado para actividad ${activity.id}:`, starterGuideData.name);
              }
            } catch (error) {
              console.warn(`[DEBUG] No se encontró starterGuide para actividad ${activity.id}:`, error.message);
            }

            // Crear documento practicalGuide con datos del starterGuide
            const practicalGuideRef = doc(db, 'practicalGuide', activity.id);
            const practicalGuideData = {
              id: activity.id,
              courseId: courseId,
              courseName: courseName,
              moduleIndex: moduleIndex,
              moduleNumber: module.number || (moduleIndex + 1).toString(),
              moduleName: module.name || module.alias || `Módulo ${module.number || moduleIndex + 1}`,
              activityIndex: activityIndex,
              
              // USAR DATOS DEL STARTERGUIDE SI EXISTE
              name: starterGuideData?.name || activity.name || 'Actividad sin nombre',
              InitialProjectDescription: starterGuideData?.InitialProjectDescription || activity.InitialProjectDescription || "",
              introduction: starterGuideData?.introduction || activity.introduction || [],
              titleIntroduction: starterGuideData?.titleIntroduction || activity.titleIntroduction || activity.name,
              gif: starterGuideData?.gif || activity.gif || '',
              
              // PROYECTO CON DATOS ACTUALIZADOS DEL STARTERGUIDE
              project: {
                code: starterGuideData?.project?.code || activity.code || "# Código del proyecto\nconsole.log('Proyecto iniciado');",
                components: starterGuideData?.project?.components || [],
                connections: starterGuideData?.project?.connections || []
              },
              
              // CÓDIGO SEPARADO (para compatibilidad)
              code: starterGuideData?.project?.code || activity.code || "# Código del proyecto\nconsole.log('Proyecto iniciado');",
              
              // PASOS ACTUALIZADOS DEL STARTERGUIDE
              steps: starterGuideData?.steps || activity.steps || [{
                description: "Configura los componentes básicos para la actividad."
              }],
              
              // Metadatos del sistema
              isActive: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };

            batch.set(practicalGuideRef, practicalGuideData);
            createdGuides.push({
              id: practicalGuideRef.id,
              activityId: activity.id,
              activityName: practicalGuideData.name,
              type: 'practicalGuide',
              hasStarterGuideData: !!starterGuideData
            });
          }
        }
      }

      if (createdGuides.length > 0) {
        await batch.commit();
        console.log(`${createdGuides.length} practicalGuides creados con datos de starterGuides`);
        
        // Log de estadísticas
        const withStarterData = createdGuides.filter(g => g.hasStarterGuideData).length;
        const withoutStarterData = createdGuides.length - withStarterData;
        console.log(`Con datos de starterGuide: ${withStarterData}, Sin datos: ${withoutStarterData}`);
      }
      
      return createdGuides;
    } catch (error) {
      console.error('Error al crear practicalGuides:', error);
      throw error;
    }
  },  
  // Función para procesar módulos y generar IDs únicos
  processModules: (modules) => {
    if (!Array.isArray(modules)) return [];
    
    return modules.map((module, moduleIndex) => {
      const processedModule = {
        ...module,
        number: (moduleIndex + 1).toString(),
        name: module.name || module.alias || `Módulo ${moduleIndex + 1}`,
        lessons: [{
          classActivities: (module.lessons?.[0]?.classActivities || []).map(activity => {
            const activityId = activity.id || `activity${CoursesDataTest.generateUniqueId()}`;
            const idStart = activity.idStart || `${activityId}Start`;
            
            return {
              ...activity,
              id: activityId,
              idStart: idStart,
              name: activity.name || 'Actividad sin nombre',
              code: activity.code || "# Código del proyecto\nconsole.log('Proyecto iniciado');",
              componentsDiagram: activity.componentsDiagram || "Diagrama de componentes pendiente",
              stepByStepGuide: activity.stepByStepGuide || "Guía paso a paso pendiente"
            };
          })
        }]
      };
      return processedModule;
    });
  },

  // Crear un nuevo curso con starterGuides automáticos
  createCourse: async (courseData) => {
    try {
      if (!courseData || !courseData.name) {
        throw new Error('Datos del curso inválidos');
      }

      // Procesar módulos y actividades para generar IDs únicos
      const processedModules = CoursesDataTest.processModules(courseData.modules || []);

      // Remover campos undefined antes de guardar
      const cleanCourseData = CoursesDataTest.cleanUndefinedFields({
        ...courseData,
        modules: processedModules,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Establecer valores por defecto para campos opcionales
        bannerImage: courseData.bannerImage || null, // null en lugar de undefined
        backgroundColor: courseData.backgroundColor || '#667eea',
        institutionLogo: courseData.institutionLogo || null,
        image: courseData.image || null,
        imageBanner: courseData.imageBanner || null
      });
      // Crear el curso
      const docRef = await addDoc(collection(db, 'academyCourses'), cleanCourseData);
      
      const courseId = docRef.id;
      
      // Decidir qué tipo de guías crear según isPublished
      if (processedModules.length > 0) {
        if (courseData.isPublished) {
          // Crear practicalGuides para curso publicado
          await CoursesDataTest.createPracticalGuidesForActivities(
            courseId, 
            courseData.name, 
            processedModules
          );
          console.log(`Curso "${courseData.name}" creado como PUBLICADO - practicalGuides generadas`);
        } else {
          // Crear starterGuides para curso en borrador
          await CoursesDataTest.createStarterGuidesForActivities(
            courseId, 
            courseData.name, 
            processedModules
          );
          console.log(`Curso "${courseData.name}" creado como BORRADOR - starterGuides generadas`);
        }
      }

      // Guardar el curso en la sub-colección academyCourses de la institución
      if (courseData.institution) {
        const institutionsSnapshot = await getDocs(collection(db, 'educationalInstitutions'));
        const institutionDoc = institutionsSnapshot.docs.find(doc => doc.data().name === courseData.institution);
        
        if (institutionDoc) {
          const courseRef = doc(db, 'educationalInstitutions', institutionDoc.id, 'academyCourses', courseId);
          await setDoc(courseRef, { 
            addedAt: new Date().toISOString(),
            name: courseData.name
          });
        }
      }
      
      // Obtener el curso creado
      const newCourse = await getDoc(docRef);
      return { id: newCourse.id, ...newCourse.data() };
    } catch (error) {
      console.error('Error al crear el curso:', error);
      throw error;
    }
  },

  // Actualizar un curso existente
  updateCourse: async (courseId, updateData) => {
    try {
      if (!courseId || !updateData) {
        throw new Error('Parámetros inválidos para actualizar curso');
      }

      const courseRef = doc(db, 'academyCourses', courseId);
      const courseSnap = await getDoc(courseRef);
      
      if (!courseSnap.exists()) {
        throw new Error('Curso no encontrado');
      }

      const currentCourseData = courseSnap.data();
      
      // Procesar módulos actualizados
      const processedModules = CoursesDataTest.processModules(updateData.modules || currentCourseData.modules || []);

      // Combinar datos existentes con nuevos datos
      const mergedData = {
        ...currentCourseData,
        ...updateData,
        modules: processedModules,
        updatedAt: new Date().toISOString()
      };

      // LIMPIAR DATOS: Remover campos undefined después del merge
      const cleanUpdateData = CoursesDataTest.cleanUndefinedFields(mergedData);

      // Actualizar el curso
      await updateDoc(courseRef, cleanUpdateData);

      // Si la institución ha cambiado, actualizar en la sub-colección
      // Solo actualizar sub-colecciones si hay un cambio real de institución
      // (no si simplemente se está preservando la misma institución)
      const institutionChanged = updateData.institution !== currentCourseData.institution && 
                               updateData.institution !== undefined;
      
      if (institutionChanged) {
        const batch = writeBatch(db);
        
        // Eliminar de la institución anterior si existe
        if (currentCourseData.institution) {
          const oldInstitutionsSnapshot = await getDocs(collection(db, 'educationalInstitutions'));
          const oldInstitutionDoc = oldInstitutionsSnapshot.docs.find(doc => doc.data().name === currentCourseData.institution);
          
          if (oldInstitutionDoc) {
            const oldCourseRef = doc(db, 'educationalInstitutions', oldInstitutionDoc.id, 'academyCourses', courseId);
            batch.delete(oldCourseRef);
          }
        }
        
        // Agregar a la nueva institución si se especificó una
        if (updateData.institution) {
          const newInstitutionsSnapshot = await getDocs(collection(db, 'educationalInstitutions'));
          const newInstitutionDoc = newInstitutionsSnapshot.docs.find(doc => doc.data().name === updateData.institution);
          
          if (newInstitutionDoc) {
            const newCourseRef = doc(db, 'educationalInstitutions', newInstitutionDoc.id, 'academyCourses', courseId);
            batch.set(newCourseRef, { 
              addedAt: new Date().toISOString(),
              name: updateData.name
            });
          }
        }
        
        await batch.commit();
      }

      // Identificar nuevas actividades y crear sus starterGuides
      const newActivities = [];
      processedModules.forEach((module, moduleIndex) => {
        if (module.lessons?.[0]?.classActivities) {
          module.lessons[0].classActivities.forEach((activity, activityIndex) => {
            // Verificar si la actividad es nueva comparando con el estado anterior
            const existingModule = currentCourseData.modules?.[moduleIndex];
            const existingActivity = existingModule?.lessons?.[0]?.classActivities?.find(a => a.id === activity.id);
            
            if (!existingActivity) {
              newActivities.push({
                module,
                moduleIndex,
                activity,
                activityIndex
              });
            }
          });
        }
      });

      // Crear starterGuides para nuevas actividades
      if (newActivities.length > 0) {
        const batch = writeBatch(db);
        
        for (const { module, moduleIndex, activity, activityIndex } of newActivities) {
          const starterGuideRef = doc(db, 'starterGuides', activity.idStart);
          const starterGuideData = {
            id: activity.idStart,
            activityId: activity.id,
            courseId: courseId,
            courseName: updateData.name || currentCourseData.name,
            moduleIndex: moduleIndex,
            moduleNumber: module.number,
            moduleName: module.name || module.alias || `Módulo ${module.number}`,
            activityIndex: activityIndex,
            activityName: activity.name,
            name: activity.name,
            InitialProjectDescription: activity.InitialProjectDescription || activity.introductionText || "",
            descriptions: activity.descriptions || `Descripción de la actividad: ${activity.name}`,
            project: {
              code: activity.code || "# Código del proyecto pendiente"
            },
            steps: activity.steps || [
              {
                description: "Configura los componentes básicos para la actividad.",
              }
            ],
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          batch.set(starterGuideRef, starterGuideData);
        }

        await batch.commit();
      }
      
      const updatedCourse = await getDoc(courseRef);
      return { id: updatedCourse.id, ...updatedCourse.data() };
    } catch (error) {
      console.error('Error al actualizar el curso:', error);
      throw error;
    }
  },

  updateCourseImage: async (courseId, imageData) => {
      try {          
          const courseRef = doc(db, 'academyCourses', courseId);
          const courseSnap = await getDoc(courseRef);
          
          if (!courseSnap.exists()) {
              throw new Error('Curso no encontrado');
          }

          
          await updateDoc(courseRef, {
              image: imageData.url,
              imagePath: imageData.path,
              updatedAt: new Date().toISOString()
          });
          return true;
      } catch (error) {
          console.error('Error al actualizar imagen del curso:', error);
          throw error;
      }
  },

  // Eliminar un curso y sus starterGuides relacionados
  deleteCourse: async (courseId) => {
    try {
      if (!courseId) {
        throw new Error('ID de curso requerido');
      }

      const batch = writeBatch(db);
      
      // Obtener el curso antes de eliminarlo para saber a qué institución pertenece
      const courseRef = doc(db, 'academyCourses', courseId);
      const courseSnap = await getDoc(courseRef);
      
      if (!courseSnap.exists()) {
        throw new Error('Curso no encontrado');
      }

      const courseData = courseSnap.data();
      
      // Eliminar el curso
      batch.delete(courseRef);
      
      // Encontrar y eliminar todos los starterGuides relacionados
      const starterGuidesQuery = query(
        collection(db, 'starterGuides'), 
        where('courseId', '==', courseId)
      );
      const starterGuidesSnapshot = await getDocs(starterGuidesQuery);
      
      starterGuidesSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      // Si el curso pertenece a una institución, eliminarlo de su sub-colección
      if (courseData.institution) {
        const institutionsSnapshot = await getDocs(collection(db, 'educationalInstitutions'));
        const institutionDoc = institutionsSnapshot.docs.find(doc => doc.data().name === courseData.institution);
        
        if (institutionDoc) {
          const institutionCourseRef = doc(db, 'educationalInstitutions', institutionDoc.id, 'academyCourses', courseId);
          batch.delete(institutionCourseRef);
        }
      }
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error al eliminar el curso:', error);
      throw error;
    }
  },

  // Agregar un módulo a un curso
  addModuleToCourse: async (courseId, moduleData) => {
    try {
      if (!courseId || !moduleData) {
        throw new Error('Parámetros inválidos para agregar módulo');
      }

      const courseRef = doc(db, 'academyCourses', courseId);
      const courseSnap = await getDoc(courseRef);
      
      if (!courseSnap.exists()) {
        throw new Error('Curso no encontrado');
      }

      const courseData = courseSnap.data();
      const modules = courseData.modules || [];
      
      // Generar número de módulo automáticamente
      const moduleNumber = (modules.length + 1).toString();
      const newModule = {
        ...moduleData,
        number: moduleNumber,
        name: moduleData.name || moduleData.alias || `Módulo ${moduleNumber}`,
        lessons: [{ 
          classActivities: (moduleData.lessons?.[0]?.classActivities || []).map(activity => {
            const activityId = activity.id || `activity${CoursesDataTest.generateUniqueId()}`;
            const idStart = activity.idStart || `${activityId}Start`;
            
            return {
              ...activity,
              id: activityId,
              idStart: idStart,
              name: activity.name || 'Actividad sin nombre',
              code: activity.code || "# Código del proyecto\nconsole.log('Proyecto iniciado');",
              componentsDiagram: activity.componentsDiagram || "Diagrama de componentes pendiente",
              stepByStepGuide: activity.stepByStepGuide || "Guía paso a paso pendiente"
            };
          })
        }]
      };
      
      modules.push(newModule);
      
      await updateDoc(courseRef, { 
        modules,
        updatedAt: new Date().toISOString()
      });

      // Crear starterGuides para las actividades del nuevo módulo
      if (newModule.lessons[0].classActivities.length > 0) {
        await CoursesDataTest.createStarterGuidesForActivities(
          courseId, 
          courseData.name, 
          [newModule]
        );
      }

      const updatedCourse = await getDoc(courseRef);
      return { id: updatedCourse.id, ...updatedCourse.data() };
    } catch (error) {
      console.error('Error al agregar módulo al curso:', error);
      throw error;
    }
  },

  // Actualizar un módulo específico
  updateModule: async (courseId, moduleIndex, moduleData) => {
    try {
      if (!courseId || moduleIndex < 0 || !moduleData) {
        throw new Error('Parámetros inválidos para actualizar módulo');
      }

      const courseRef = doc(db, 'academyCourses', courseId);
      const courseSnap = await getDoc(courseRef);
      
      if (!courseSnap.exists()) {
        throw new Error('Curso no encontrado');
      }

      const courseData = courseSnap.data();
      const modules = [...courseData.modules];
      
      if (moduleIndex >= 0 && moduleIndex < modules.length) {
        modules[moduleIndex] = { 
          ...modules[moduleIndex], 
          ...moduleData,
          name: moduleData.name || moduleData.alias || modules[moduleIndex].name
        };
        
        await updateDoc(courseRef, { 
          modules,
          updatedAt: new Date().toISOString()
        });
      } else {
        throw new Error('Índice de módulo inválido');
      }

      const updatedCourse = await getDoc(courseRef);
      return { id: updatedCourse.id, ...updatedCourse.data() };
    } catch (error) {
      console.error('Error al actualizar módulo:', error);
      throw error;
    }
  },

  // Eliminar un módulo y sus starterGuides relacionados
  removeModuleFromCourse: async (courseId, moduleIndex) => {
    try {
      if (!courseId || moduleIndex < 0) {
        throw new Error('Parámetros inválidos para remover módulo');
      }

      const courseRef = doc(db, 'academyCourses', courseId);
      const courseSnap = await getDoc(courseRef);
      
      if (!courseSnap.exists()) {
        throw new Error('Curso no encontrado');
      }

      const courseData = courseSnap.data();
      const modules = courseData.modules || [];
      
      if (moduleIndex >= 0 && moduleIndex < modules.length) {
        const moduleToRemove = modules[moduleIndex];
        
        // Eliminar starterGuides relacionados con este módulo
        if (moduleToRemove.lessons?.[0]?.classActivities) {
          const batch = writeBatch(db);
          
          for (const activity of moduleToRemove.lessons[0].classActivities) {
            if (activity.id) {
              const starterGuidesQuery = query(
                collection(db, 'starterGuides'), 
                where('activityId', '==', activity.id)
              );
              const starterGuidesSnapshot = await getDocs(starterGuidesQuery);
              
              starterGuidesSnapshot.docs.forEach((doc) => {
                batch.delete(doc.ref);
              });
            }
          }
          
          if (starterGuidesSnapshot?.docs?.length > 0) {
            await batch.commit();
          }
        }
        
        // Filtrar el módulo del array
        modules.splice(moduleIndex, 1);
        
        // Renumerar los módulos restantes
        modules.forEach((module, index) => {
          module.number = (index + 1).toString();
        });
        
        await updateDoc(courseRef, { 
          modules,
          updatedAt: new Date().toISOString()
        });
      } else {
        throw new Error('Índice de módulo inválido');
      }

      const updatedCourse = await getDoc(courseRef);
      return { id: updatedCourse.id, ...updatedCourse.data() };
    } catch (error) {
      console.error('Error al remover módulo del curso:', error);
      throw error;
    }
  },

  // Crear starterGuide individual
  createSingleStarterGuide: async (courseId, courseName, module, moduleIndex, activity, activityIndex) => {
    try {
      if (!activity.idStart || !activity.id) {
        throw new Error('Actividad debe tener ID y idStart válidos');
      }

      // Usar doc() con el idStart como ID específico del documento
      const starterGuideRef = doc(db, 'starterGuides', activity.idStart);
      const starterGuideData = {
        // Campos principales de identificación
        id: activity.idStart,
        activityId: activity.id,
        courseId: courseId,
        courseName: courseName,
        moduleIndex: moduleIndex,
        moduleNumber: module.number || (moduleIndex + 1).toString(),
        moduleName: module.name || module.alias || `Módulo ${module.number || moduleIndex + 1}`,
        activityIndex: activityIndex,
        activityName: activity.name || 'Actividad sin nombre',
        
        // Estructura del contenido del starterGuide
        name: activity.name || 'Actividad sin nombre',
        InitialProjectDescription: activity.InitialProjectDescription || activity.introductionText || `En esta actividad trabajaremos con ${activity.name}`,
        descriptions: activity.descriptions || `Descripción de la actividad: ${activity.name}`,

        
        // Proyecto con código
        project: {
          code: activity.code || "# Código del proyecto\nconsole.log('Proyecto iniciado');"
        },
        
        // Pasos de la guía
        steps: activity.steps || [
          {
            description: "¡Ahora puedes comenzar a realizar el paso a paso de la guía para esta actividad!",
          }
        ],
        
        // Metadatos
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Usar setDoc en lugar de addDoc para especificar el ID del documento
      await setDoc(starterGuideRef, starterGuideData);
      return starterGuideData;
    } catch (error) {
      console.error('Error al crear starterGuide individual:', error);
      throw error;
    }
  },

  async validatePracticalGuideCreation(courseId) {
    try {
        const course = await CoursesDataTest.getCourseById(courseId);
        const validation = [];
        
        for (let moduleIndex = 0; moduleIndex < course.modules.length; moduleIndex++) {
            const module = course.modules[moduleIndex];
            
            if (module.lessons?.[0]?.classActivities) {
                for (const activity of module.lessons[0].classActivities) {
                    const result = {
                        activityId: activity.id,
                        activityName: activity.name,
                        hasIdStart: !!activity.idStart,
                        starterGuideExists: false,
                        practicalGuideExists: false,
                        starterGuideData: null
                    };
                    
                    // Verificar si existe starterGuide
                    if (activity.idStart) {
                        try {
                            const starterGuide = await CoursesDataTest.getStarterGuideByIdStart(activity.idStart);
                            result.starterGuideExists = true;
                            result.starterGuideData = {
                                hasSteps: !!(starterGuide.steps && starterGuide.steps.length > 0),
                                hasCode: !!(starterGuide.project?.code),
                                hasComponents: !!(starterGuide.project?.components && starterGuide.project.components.length > 0),
                                stepsCount: starterGuide.steps?.length || 0,
                                componentsCount: starterGuide.project?.components?.length || 0
                            };
                        } catch (error) {
                            result.starterGuideExists = false;
                        }
                    }
                    
                    // Verificar si existe practicalGuide
                    try {
                        const practicalGuideRef = doc(db, 'practicalGuide', activity.id);
                        const practicalGuideSnap = await getDoc(practicalGuideRef);
                        result.practicalGuideExists = practicalGuideSnap.exists();
                    } catch (error) {
                        result.practicalGuideExists = false;
                    }
                    
                    validation.push(result);
                }
            }
        }
        
        return validation;
    } catch (error) {
        console.error('Error en validación:', error);
        throw error;
    }
},


  // Agregar actividad a un módulo específico
  addActivityToModule: async (courseId, moduleIndex, activityData) => {
    try {
      if (!courseId || moduleIndex < 0 || !activityData) {
        throw new Error('Parámetros inválidos para agregar actividad');
      }

      const courseRef = doc(db, 'academyCourses', courseId);
      const courseSnap = await getDoc(courseRef);

      if (!courseSnap.exists()) {
        throw new Error('Curso no encontrado');
      }

      const courseData = courseSnap.data();
      const modules = [...courseData.modules];

      if (moduleIndex >= 0 && moduleIndex < modules.length) {
        if (!modules[moduleIndex].lessons) {
          modules[moduleIndex].lessons = [{ classActivities: [] }];
        }
        
        if (!modules[moduleIndex].lessons[0].classActivities) {
          modules[moduleIndex].lessons[0].classActivities = [];
        }
        
        // Generar ID único para la actividad
        const activityId = `activity${CoursesDataTest.generateUniqueId()}`;
        const idStart = `${activityId}Start`;
        
        const newActivity = {
          ...activityData,
          id: activityId,
          idStart: idStart,
          name: activityData.name || 'Actividad sin nombre',
          code: activityData.code || "# Código del proyecto\nconsole.log('Proyecto iniciado');",
          componentsDiagram: activityData.componentsDiagram || "Diagrama de componentes pendiente",
          stepByStepGuide: activityData.stepByStepGuide || "Guía paso a paso pendiente"
        };
        
        // Agregar actividad al módulo
        modules[moduleIndex].lessons[0].classActivities.push(newActivity);
        
        // Actualizar el curso
        await updateDoc(courseRef, { 
          modules,
          updatedAt: new Date().toISOString()
        });

        // Crear starterGuide para la nueva actividad con estructura correcta
        await CoursesDataTest.createSingleStarterGuide(
          courseId,
          courseData.name,
          modules[moduleIndex],
          moduleIndex,
          newActivity,
          modules[moduleIndex].lessons[0].classActivities.length - 1
        );
      } else {
        throw new Error('Índice de módulo inválido');
      }

      const updatedCourse = await getDoc(courseRef);
      return { id: updatedCourse.id, ...updatedCourse.data() };
    } catch (error) {
      console.error('Error al agregar actividad al módulo:', error);
      throw error;
    }
  },

  // Función auxiliar para obtener una guía de inicio por idStart
  getStarterGuideByIdStart: async (idStart) => {
    try {
      if (!idStart) {
        throw new Error('idStart requerido');
      }

      const starterGuideRef = doc(db, 'starterGuides', idStart);
      const starterGuideSnap = await getDoc(starterGuideRef);
      
      if (starterGuideSnap.exists()) {
        return { docId: starterGuideSnap.id, ...starterGuideSnap.data() };
      } else {
        throw new Error('Guía de inicio no encontrada');
      }
    } catch (error) {
      console.error('Error al obtener guía de inicio:', error);
      throw error;
    }
  },

  // Función auxiliar para obtener todas las guías de un curso
  getStarterGuidesByCourseId: async (courseId) => {
    try {
      if (!courseId) {
        throw new Error('courseId requerido');
      }

      const q = query(collection(db, 'starterGuides'), where('courseId', '==', courseId));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        docId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error al obtener guías del curso:', error);
      throw error;
    }
  },

  // Función auxiliar para actualizar una guía de inicio
  updateStarterGuide: async (idStart, updateData) => {
    try {
      if (!idStart || !updateData) {
        throw new Error('Parámetros inválidos para actualizar guía');
      }

      const starterGuideRef = doc(db, 'starterGuides', idStart);
      const starterGuideSnap = await getDoc(starterGuideRef);

      if (!starterGuideSnap.exists()) {
        throw new Error('Guía de inicio no encontrada');
      }

      // Asegurar que la estructura sea correcta al actualizar
      const updatedData = {
        ...updateData,
        updatedAt: new Date().toISOString()
      };
      
      // Si se está actualizando el proyecto, asegurar que tenga la estructura correcta
      if (updateData.project && typeof updateData.project === 'string') {
        updatedData.project = { code: updateData.project };
      }
      
      // Si se están actualizando los pasos, validar estructura
      if (updateData.steps && Array.isArray(updateData.steps)) {
        updatedData.steps = updateData.steps.map(step => ({
          stepName: step.stepName || "Paso sin nombre",
          description: step.description || "",
          details: step.details || [],
          explanation: step.explanation || "",
          image: step.image || "https://firebasestorage.googleapis.com/v0/b/mvp-inventech-testing.appspot.com/o/images%2FimageDefault.png?alt=media&token=456a8bd7-1b1b-4973-bde2-c378e4b6a777",
          ...step
        }));
      }
      
      await updateDoc(starterGuideRef, updatedData);
      
      const updatedDoc = await getDoc(starterGuideRef);
      return { docId: updatedDoc.id, ...updatedDoc.data() };
    } catch (error) {
      console.error('Error al actualizar guía de inicio:', error);
      throw error;
    }
  }
};

export default CoursesDataTest;